import React, { useState, useEffect } from 'react';
import { authFetch } from '../../utils/authFetch';
import TemplateSelector from '../Message/TemplateSelector';
import BroadcastTemplate from './BroadcastTemplate';

interface Contact {
  id: string;
  phoneNumber: string;
  name?: string;
  avatar?: string;
}

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: any[];
}

interface NewMessageProps {
  onClose: () => void;
  onStartChat: (phoneNumber: string, contactName?: string) => void;
}

const NewMessage: React.FC<NewMessageProps> = ({ onClose, onStartChat }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'contacts' | 'new' | 'template'>('contacts');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadContacts();
  }, []);

  // Debounce search - reload contacts when search term changes
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      loadContacts();
    }, 300); // Wait 300ms after user stops typing
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      // authFetch automatically prepends /api
      let url = '/contacts?limit=100'; // Load up to 100 contacts for the dropdown
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContacts(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelect = (contact: Contact) => {
    onStartChat(contact.phoneNumber, contact.name);
  };

  const handleNewNumber = () => {
    if (!phoneNumber.trim()) return;
    
    // Basic phone number validation
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    if (cleanNumber.length < 10) {
      alert('Please enter a valid phone number');
      return;
    }
    
    onStartChat(phoneNumber.trim());
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '600px' }}>
        <div className="assignment-header">
          <h4>New Message</h4>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        
        <div className="assignment-content">
          <div className="panel-tabs" style={{ marginBottom: '20px' }}>
            <button
              className={`panel-tab ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              Existing Contacts
            </button>
            <button
              className={`panel-tab ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => setActiveTab('new')}
            >
              New Number
            </button>
            <button
              className={`panel-tab ${activeTab === 'template' ? 'active' : ''}`}
              onClick={() => setActiveTab('template')}
            >
              📱 Send Template
            </button>
          </div>

          {activeTab === 'contacts' ? (
            <>
              <div className="search-container" style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Search contacts..."
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Loading contacts...
                  </div>
                ) : contacts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    {searchTerm ? 'No contacts found' : 'No contacts available'}
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="conversation-item"
                      onClick={() => handleContactSelect(contact)}
                      style={{ cursor: 'pointer', marginBottom: '8px' }}
                    >
                      <div className="avatar" style={{ marginRight: '12px' }}>
                        {contact.avatar ? (
                          <img src={contact.avatar} alt={contact.name || contact.phoneNumber} />
                        ) : (
                          getInitials(contact.name || contact.phoneNumber)
                        )}
                      </div>
                      <div className="conversation-info">
                        <div className="conversation-header">
                          <h6 className="contact-name">
                            {contact.name || contact.phoneNumber}
                          </h6>
                        </div>
                        <div className="last-message" style={{ fontSize: '12px', color: '#6b7280' }}>
                          {contact.phoneNumber}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : activeTab === 'new' ? (
            <div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter phone number (e.g., +1234567890)"
                  className="form-input"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Include country code (e.g., +62 for Indonesia)
                </div>
              </div>
              
              <button
                onClick={handleNewNumber}
                className="assign-button"
                disabled={!phoneNumber.trim()}
                style={{ 
                  width: '100%',
                  opacity: phoneNumber.trim() ? 1 : 0.5,
                  cursor: phoneNumber.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Start Conversation
              </button>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
                📱 Select a template to send to one or multiple contacts
              </div>
              <button
                onClick={() => setShowTemplateSelector(true)}
                className="assign-button"
                style={{ width: '100%' }}
              >
                Choose Template
              </button>
              {selectedTemplate && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                    Selected: {selectedTemplate.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 0'
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template Selector */}
      {showTemplateSelector && (
        <TemplateSelector
          onClose={() => setShowTemplateSelector(false)}
          onSelect={(template) => {
            setSelectedTemplate(template);
            setShowTemplateSelector(false);
          }}
        />
      )}

      {/* Broadcast Template - for sending to selected contacts */}
      {selectedTemplate && (
        <BroadcastTemplate
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSent={() => {
            setSelectedTemplate(null);
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default NewMessage;
